<template>
  <div v-if="hasReviews" class="counselor-reviews">
    <div class="counselor-reviews__header">
      <h2 v-text="texts.verifiedReviews" />

      <rating-stars
        :rating="rating"
        :show-rating-text="true"
        class="counselor-reviews__rating"
      />

      <p class="counselor-reviews__rating-text" v-html="ratingString" />

      <div class="counselor-reviews__breakdown">
        <div
          v-for="star in [5, 4, 3, 2, 1]"
          :key="star"
          class="counselor-reviews__breakdown-row"
        >
          <span class="counselor-reviews__breakdown-stars">{{ star }}</span>
          <div class="counselor-reviews__breakdown-bar-container">
            <div
              class="counselor-reviews__breakdown-bar"
              :style="{ width: ratingPercentages[star].percentage + '%' }"
            />
          </div>
        </div>
      </div>
    </div>

    <transition-group
      ref="grid"
      class="counselor-reviews__list"
      name="counselor-reviews__list"
      tag="div"
    >
      <review-card
        v-for="(review, index) of reviewsToShow"
        :key="`${review.id}-${index}`"
        :post="review"
        variant="card"
      />
    </transition-group>

    <button
      v-if="!maxedOut"
      class="btn outline counselor-reviews__show-more"
      @click="showMoreReviews"
      v-text="texts.showMoreRatingsText"
    />

    <review-modal v-if="modalReview" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { sprintf } from 'sprintf-js';
import { mapState } from 'vuex';
import {
  formatAverageRating,
  formatNumberOfRatings,
} from 'Main/reviews/misc/helpers';
import { TReview } from 'Stores/modules/types/user.types';
import { useIsMobile } from 'Main/composables/useIsMobile';
import ReviewCard from 'Main/reviews/components/ReviewCard.vue';
import ReviewModal from 'Main/reviews/components/ReviewModal.vue';
import RatingStars from 'Components/RatingStars.vue';

export default defineComponent({
  name: 'CounselorReviews',

  components: {
    ReviewCard,
    ReviewModal,
    RatingStars,
  },

  setup() {
    const { isMobile, isTablet, isMobileDevice } = useIsMobile();
    return { isMobile, isTablet, isMobileDevice };
  },

  data() {
    return {
      trustpilotLink: window.theme_vars.trustpilotUrl,
      texts: window.theme_vars.trustpilot,
      currentSite: window.theme_vars.currentSite,
      pagesShown: 1,
    };
  },

  computed: {
    ...mapState('user', [
      'name',
      'reviews',
      'rating',
      'totalReviews',
      'reviewsDistribution',
    ]),
    ...mapState('reviews', ['modalReview']),

    hasReviews() {
      return Array.isArray(this.reviews) && this.reviews.length > 0;
    },

    averageRating() {
      return formatAverageRating(this.rating);
    },

    numberOfRatings() {
      return formatNumberOfRatings(this.totalReviews);
    },

    ratingString() {
      return sprintf(this.texts.ratingTextTotalScore, this.numberOfRatings);
    },

    ratingPercentages() {
      const percentages: Record<number, { count: number; percentage: number }> =
        {};
      const total = this.totalReviews || 0;

      // Always initialize all star ratings (1-5) with safe defaults
      for (let i = 1; i <= 5; i++) {
        const count = this.reviewsDistribution?.[i] ?? 0;
        const percentage = total > 0 ? (count / total) * 100 : 0;

        percentages[i] = {
          count: count,
          percentage: percentage,
        };
      }

      return percentages;
    },

    pagedReviews() {
      if (!Array.isArray(this.reviews)) {
        return [];
      }

      const result: TReview[][] = [];

      for (let i = 0; i < this.reviews.length; i += this.reviewsPerPage) {
        result.push(this.reviews.slice(i, i + this.reviewsPerPage));
      }

      return result;
    },

    reviewsToShow() {
      if (!this.pagedReviews.length) {
        return [];
      }

      return this.pagedReviews.slice(0, this.pagesShown).flat();
    },

    maxedOut() {
      return this.pagesShown >= this.pagedReviews.length;
    },

    reviewsPerPage() {
      return this.isMobile ? 3 : 6;
    },
  },

  methods: {
    showMoreReviews() {
      this.pagesShown++;
    },
  },
});
</script>
